<template>
  <OverlayPanel :show="isOpen" :label="placeholder" @close="close">
      <div class="search-input-row">
        <font-awesome-icon icon="search" class="search-icon text-secondary"/>
        <input
            ref="input"
            v-model="query"
            class="search-input"
            type="text"
            autocomplete="off"
            spellcheck="false"
            data-no-shortcuts
            :placeholder="placeholder"
            @input="onQueryChange"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="selectActive"
        />
        <font-awesome-icon v-if="loading" icon="spinner" spin class="text-secondary"/>
      </div>

      <div v-if="query.trim().length >= minLength" class="search-results" ref="results">
        <div v-for="group in groups" :key="group.provider.key">
          <div v-if="showGroupTitles" class="search-group-title" :class="group.provider.colorClass || 'text-secondary'">
            {{ group.provider.title }}
          </div>
          <div
              v-for="entry in group.entries"
              :key="`${group.provider.key}-${entry.item.id}`"
              class="search-result"
              :class="{active: entry.index === activeIndex}"
              :data-index="entry.index"
              @mousemove="activeIndex = entry.index"
              @click="select(entry.index)"
          >
            <font-awesome-icon v-if="entry.item.icon || group.provider.icon" :icon="entry.item.icon || group.provider.icon"
                               class="result-icon" :class="group.provider.colorClass"/>
            <span class="result-title">{{ entry.item.title }}</span>
            <span v-if="entry.item.subtitle" class="result-subtitle">{{ entry.item.subtitle }}</span>
          </div>
        </div>
        <div v-if="!loading && results.length === 0" class="search-empty text-secondary">No results</div>
      </div>
  </OverlayPanel>
</template>

<script>
/**
 * A Spotlight-style search box in the middle of the page.
 *
 * It searches through providers, one per element type. Open it with one provider to search one type, or with several for a
 * general search. A provider is:
 *
 *   {
 *     key: 'users',                // unique
 *     title: 'Users',              // the group title, shown when searching several providers
 *     icon: 'user',                // optional font-awesome icon of each result
 *     colorClass: 'text-runner',   // optional class coloring the icon and the group title, from assets/css/main.css
 *     search(query): Promise<[{id, title, subtitle, icon}]>,   // icon: optional, in place of the provider's
 *     select(item),                // called with the chosen result, after the box closes
 *   }
 */
import OverlayPanel from "@/components/Overlay/OverlayPanel";

const DEBOUNCE_MS = 200;
// Reopening the same search within this time brings back its query, results and selection, e.g. to pick another result
// after choosing the wrong one
const RESTORE_MS = 10 * 1000;

export default {
  name: 'GlobalSearch',
  components: {OverlayPanel},
  data() {
    return {
      isOpen: false,
      providers: [],
      groupTitles: null,
      placeholder: 'Search',
      minLength: 1,
      query: '',
      // [{provider, item}], in display order
      results: [],
      activeIndex: 0,
      loading: false,
      // Return was hit while searching
      selectWhenLoaded: false,
      // Only the latest search updates the results
      searchId: 0,
      debounceTimer: null,
      // What the box showed when it last closed: {kind, query, results: [{providerKey, item}], activeIndex, stale, closedAt}
      lastState: null,
    }
  },
  computed: {
    showGroupTitles() {
      return this.groupTitles === null ? this.providers.length > 1 : this.groupTitles;
    },
    groups() {
      return this.providers
          .map(provider => ({
            provider,
            entries: this.results
                .map((result, index) => ({...result, index}))
                .filter(result => result.provider === provider),
          }))
          .filter(group => group.entries.length > 0);
    },
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer);
    document.removeEventListener('focusin', this.keepFocus);
  },
  methods: {
    /**
     * @param providers The providers to search, see above
     * @param placeholder The input's placeholder
     * @param minLength The query length that starts a search. 0 lists the results at once, before anything is typed.
     * @param groupTitles Whether to show each provider's title over its results. By default, when there are several.
     */
    open({providers, placeholder = 'Search', minLength = 1, groupTitles = null}) {
      this.providers = providers;
      this.groupTitles = groupTitles;
      this.placeholder = placeholder;
      this.minLength = minLength;
      this.query = '';
      this.results = [];
      this.activeIndex = 0;
      this.loading = false;
      this.selectWhenLoaded = false;
      const restored = this.restore();
      if (!restored && minLength === 0) {
        // Everything, until a query narrows it down, e.g. the pages to go to
        this.search('');
      }
      this.isOpen = true;
      // The menu that opened the search returns the focus to its toggle when it closes: keep it in the input instead
      document.addEventListener('focusin', this.keepFocus);
      this.$nextTick(() => {
        this.focusInput();
        if (restored) {
          // Typing replaces the old query, the arrows pick another result
          this.$refs.input.select();
          this.scrollToActive();
        }
      });
    },
    close() {
      clearTimeout(this.debounceTimer);
      document.removeEventListener('focusin', this.keepFocus);
      this.searchId++;
      this.lastState = {
        kind: this.kind(),
        query: this.query,
        results: this.results.map(result => ({providerKey: result.provider.key, item: result.item})),
        activeIndex: this.activeIndex,
        // Closed before the results of the last keystrokes arrived
        stale: this.loading,
        closedAt: Date.now(),
      };
      this.isOpen = false;
    },
    /**
     * The same kind of search: the same providers
     */
    kind() {
      return this.providers.map(provider => provider.key).join(',');
    },
    /**
     * Brings back the last query, results and selection, if this kind of search closed a moment ago
     *
     * @return Whether it did
     */
    restore() {
      const state = this.lastState;
      this.lastState = null;
      if (!state || state.kind !== this.kind() || Date.now() - state.closedAt > RESTORE_MS || !state.query.trim()) {
        return false;
      }
      this.query = state.query;
      // The providers are new objects on each opening: match them by key
      this.results = state.results
          .map(result => ({provider: this.providers.find(provider => provider.key === result.providerKey), item: result.item}))
          .filter(result => result.provider);
      this.activeIndex = Math.min(state.activeIndex, Math.max(this.results.length - 1, 0));
      if (state.stale) {
        this.onQueryChange();
      }
      return true;
    },
    focusInput() {
      if (this.$refs.input) {
        this.$refs.input.focus();
      }
    },
    scrollToActive() {
      const element = this.$refs.results && this.$refs.results.querySelector(`[data-index="${this.activeIndex}"]`);
      if (element) {
        element.scrollIntoView({block: 'nearest'});
      }
    },
    keepFocus(event) {
      if (this.isOpen && !this.$el.contains(event.target)) {
        this.focusInput();
      }
    },
    onQueryChange() {
      clearTimeout(this.debounceTimer);
      this.selectWhenLoaded = false;
      const query = this.query.trim();
      if (query.length < this.minLength) {
        this.searchId++;
        this.results = [];
        this.loading = false;
        return;
      }
      this.loading = true;
      this.debounceTimer = setTimeout(() => this.search(query), DEBOUNCE_MS);
    },
    search(query) {
      const searchId = ++this.searchId;
      // A failing provider doesn't hide the results of the others
      const searches = this.providers.map(provider =>
          Promise.resolve()
              .then(() => provider.search(query))
              .then(items => (items || []).map(item => ({provider, item})))
              .catch(e => {
                console.warn(e);
                return [];
              })
      );
      Promise.all(searches).then(resultsByProvider => {
        if (searchId !== this.searchId) {
          return;
        }
        this.results = resultsByProvider.flat();
        this.activeIndex = 0;
        this.loading = false;
        if (this.selectWhenLoaded) {
          this.selectWhenLoaded = false;
          this.select(0);
        }
      });
    },
    move(direction) {
      if (this.results.length === 0) {
        return;
      }
      this.activeIndex = (this.activeIndex + direction + this.results.length) % this.results.length;
      this.$nextTick(this.scrollToActive);
    },
    selectActive() {
      if (this.loading) {
        // The results shown are for an older query: pick the first result of this one when it arrives
        this.selectWhenLoaded = true;
        return;
      }
      this.select(this.activeIndex);
    },
    select(index) {
      const result = this.results[index];
      if (!result) {
        return;
      }
      // Remembered as the selection, if the search is reopened
      this.activeIndex = index;
      this.close();
      result.provider.select(result.item);
    },
  },
}
</script>

<style scoped>
.search-input-row {
  display: flex;
  align-items: center;
  padding: 12px 16px;
}

.search-icon {
  font-size: 1.4rem;
  margin-right: 12px;
}

.search-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 1.5rem;
  user-select: text;
}

.search-results {
  max-height: 50vh;
  overflow-y: auto;
  border-top: 1px solid #e5e5e5;
  padding: 6px 0;
}

.search-group-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  padding: 6px 16px 2px;
}

.search-result {
  display: flex;
  align-items: baseline;
  padding: 6px 16px;
  margin: 0 6px;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
}

.search-result.active {
  background-color: #007bff;
  color: #fff;
}

.result-icon {
  margin-right: 10px;
  flex-shrink: 0;
}

/* On the selected row's blue, the type color would clash: white like the text */
.search-result.active .result-icon {
  color: inherit;
}

/*
 * The title takes the room it needs, and is cut only when it's longer than the whole row.
 * The subtitle (e.g. an email) gives way first: its large shrink factor makes it shrink before the title does.
 */
.result-title {
  font-weight: 500;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-subtitle {
  margin-left: 10px;
  opacity: 0.7;
  flex: 0 1000 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-empty {
  padding: 8px 16px;
}
</style>
